// Shared rule-evaluation logic for project custom filters.
// Used by ProjectBoardPage (board / list filtering), CustomFiltersMenu and
// ProjectFiltersManager (live "Matches N issues" preview count).
// Single source of truth so the dialog count never disagrees with the board.

const withinMs = (days) => Number(days) * 86400000;

// Timezone-aware date-only string ('YYYY-MM-DD') for a timestamp. Used for
// absolute date-range filters so "created between Jun 1 – Jun 5" is judged in
// the workspace timezone, not the viewer's browser locale.
function dateStrInTz(iso, tz) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: tz || 'UTC' });
  } catch {
    return String(iso).slice(0, 10);
  }
}

export function matchesRule(task, rules, opts = {}) {
  if (!rules) return true;
  const r = rules;
  const tz = opts.timezone || 'UTC';
  const now = Date.now();

  if (r.statuses?.length && !r.statuses.includes(task.status)) return false;
  if (r.priorities?.length && !r.priorities.includes(task.priority)) return false;
  if (r.work_types?.length && !r.work_types.includes(task.work_type || 'task')) return false;
  if (r.assignee_ids?.length && !(task.assignee_id && r.assignee_ids.includes(task.assignee_id))) return false;

  // Created — relative window
  if (r.created_within_days) {
    const ts = task.created_at ? Date.parse(task.created_at) : 0;
    if (!ts || (now - ts) > withinMs(r.created_within_days)) return false;
  }
  // Created — absolute range (tz-aware, date granularity)
  if (r.created_from || r.created_to) {
    const d = dateStrInTz(task.created_at, tz);
    if (!d) return false;
    if (r.created_from && d < r.created_from) return false;
    if (r.created_to && d > r.created_to) return false;
  }

  // Updated — relative window
  if (r.updated_within_days) {
    const ts = Date.parse(task.last_activity_at || task.updated_at || task.created_at || 0);
    if (!ts || (now - ts) > withinMs(r.updated_within_days)) return false;
  }
  // Updated — absolute range
  if (r.updated_from || r.updated_to) {
    const d = dateStrInTz(task.last_activity_at || task.updated_at || task.created_at, tz);
    if (!d) return false;
    if (r.updated_from && d < r.updated_from) return false;
    if (r.updated_to && d > r.updated_to) return false;
  }
  if (r.updated_by_ids?.length) {
    const by = task.last_activity_by || task.reporter_id;
    if (!by || !r.updated_by_ids.includes(by)) return false;
  }

  if (r.commented_within_days) {
    const ts = task.last_comment_at ? Date.parse(task.last_comment_at) : 0;
    if (!ts || (now - ts) > withinMs(r.commented_within_days)) return false;
  }
  if (r.commented_by_ids?.length) {
    if (!task.last_comment_by || !r.commented_by_ids.includes(task.last_comment_by)) return false;
  }

  // Tags — task must carry at least one of the selected tags.
  if (r.tags?.length) {
    const tt = task.tags || [];
    if (!r.tags.some(tag => tt.includes(tag))) return false;
  }

  // Reporter — any of the selected members.
  if (r.reporter_ids?.length) {
    if (!(task.reporter_id && r.reporter_ids.includes(task.reporter_id))) return false;
  }

  // Due date — relative window (from today) or absolute range.
  if (r.due_within_days) {
    const ts = task.due_date ? Date.parse(task.due_date) : 0;
    if (!ts || Math.abs(ts - now) > withinMs(r.due_within_days)) return false;
  }
  if (r.due_from || r.due_to) {
    const d = task.due_date ? String(task.due_date).slice(0, 10) : null;
    if (!d) return false;
    if (r.due_from && d < r.due_from) return false;
    if (r.due_to && d > r.due_to) return false;
  }

  // Custom fields — one typed condition per field_id (JQL-style "all fields").
  // Condition shape is inferred: { in:[…] } select, { contains:'…' } text,
  // { min,max } number, { within_days|from|to } date.
  if (r.custom_fields && typeof r.custom_fields === 'object') {
    for (const [fid, cond] of Object.entries(r.custom_fields)) {
      if (!cond || typeof cond !== 'object') continue;
      const val = (task.custom_fields || {})[fid];
      if (Array.isArray(cond.in) && cond.in.length) {
        if (val == null || !cond.in.includes(String(val))) return false;
      } else if (cond.contains != null && String(cond.contains) !== '') {
        const text = String(val == null ? '' : val).replace(/<[^>]*>/g, ' ');
        if (!text.toLowerCase().includes(String(cond.contains).toLowerCase())) return false;
      } else if (cond.min != null || cond.max != null) {
        const n = Number(val);
        if (val == null || val === '' || Number.isNaN(n)) return false;
        if (cond.min != null && cond.min !== '' && n < Number(cond.min)) return false;
        if (cond.max != null && cond.max !== '' && n > Number(cond.max)) return false;
      } else if (cond.within_days || cond.from || cond.to) {
        const d = val ? String(val).slice(0, 10) : null;
        if (!d) return false;
        if (cond.within_days) {
          const ts = Date.parse(val);
          if (!ts || (now - ts) > withinMs(cond.within_days)) return false;
        }
        if (cond.from && d < cond.from) return false;
        if (cond.to && d > cond.to) return false;
      }
    }
  }
  return true;
}

// AND-combine: a task passes only when it matches EVERY rule set in the list.
export function matchesAllRules(task, rulesList = [], opts = {}) {
  return rulesList.every(r => matchesRule(task, r, opts));
}

export function countMatches(tasks, rules, opts = {}) {
  if (!rules) return tasks.length;
  return tasks.filter(t => matchesRule(t, rules, opts)).length;
}
